/** Billing API — periods, bills, generation, state transitions, public link. */
import { http } from "@/lib/api";

export interface BillingPeriod {
  id: number;
  period_start: string;
  period_end: string;
  due_date: string;
  status: string;
  bill_count: number;
  total_amount: number;
}

export interface BillRow {
  id: number;
  house_id: string | null;
  locality: string | null;
  consumption_m3: number;
  total_amount: number;
  total_due: number;
  status: string;
  due_date: string | null;
  days_overdue: number | null;
}

/** System-wide billing overview — one definition of each figure, shared by
 * the KPI strip and anything else that needs it. Mirrors
 * `dashboard_api.features.billing.models.BillingKpis`. */
export interface BillingKpis {
  total_outstanding: number;
  total_overdue: number;
  overdue_count: number;
  total_collected: number;
  collection_rate_pct: number;
}

/** One bill in a house's real billing history, across every period — the
 * ground truth shown on SensorDetailPage's Billing tab alongside (never in
 * place of) the shadow-bill *estimate* `customer/analytics.py` computes
 * there. Mirrors `dashboard_api.features.billing.models.HouseBillRow`. */
export interface HouseBillRow {
  id: number;
  period_start: string;
  period_end: string;
  consumption_m3: number;
  total_amount: number;
  total_due: number;
  status: string;
  due_date: string | null;
  days_overdue: number | null;
  public_token: string | null;
}

export interface BillLineItem {
  block_label: string;
  consumed_qty: number;
  rate_per_unit: number;
  amount: number;
}

export interface BillDetail {
  id: number;
  house_id: string | null;
  sensor_id: string | null;
  locality: string | null;
  period_start: string | null;
  period_end: string | null;
  consumption_m3: number;
  water_charge: number;
  sewerage_charge: number;
  fixed_charge: number;
  tax_charge: number;
  adjustments: number;
  previous_balance: number;
  total_amount: number;
  total_due: number;
  paid_amount: number | null;
  read_quality: string;
  status: string;
  issued_at: string | null;
  paid_at: string | null;
  due_date: string | null;
  lines: BillLineItem[];
  public_token: string | null;
}

export const fetchPeriods = async (s?: AbortSignal): Promise<BillingPeriod[]> =>
  (await http.get<BillingPeriod[]>("/billing/periods", { signal: s })).data;

export const fetchPeriodBills = async (
  id: number,
  locality?: string | null,
  s?: AbortSignal,
): Promise<BillRow[]> =>
  (
    await http.get<BillRow[]>(`/billing/period/${id}/bills`, {
      params: locality ? { locality } : undefined,
      signal: s,
    })
  ).data;

export const fetchBillingKpis = async (s?: AbortSignal): Promise<BillingKpis> =>
  (await http.get<BillingKpis>("/billing/kpis", { signal: s })).data;

export const fetchHouseBills = async (
  houseId: string,
  s?: AbortSignal,
): Promise<HouseBillRow[]> =>
  (await http.get<HouseBillRow[]>(`/billing/house/${houseId}/bills`, { signal: s })).data;

export const issueDraftBills = async (periodId: number): Promise<{ issued: number }> =>
  (await http.post<{ issued: number }>(`/billing/period/${periodId}/issue-drafts`)).data;

export const fetchBill = async (id: number, s?: AbortSignal): Promise<BillDetail> =>
  (await http.get<BillDetail>(`/billing/bill/${id}`, { signal: s })).data;

export interface GeneratePeriodResult {
  period_id: number | null;
  bills_created: number;
  agreements?: number;
  errors?: number;
  /** Bills this period already held *before* this call — lets the caller
   * tell "nothing to bill" (0 active accounts) apart from "already fully
   * billed" (every eligible account has a bill for this period already). */
  already_billed_count?: number;
}

export const generatePeriod = async (body: {
  period_start: string;
  period_end: string;
  due_date: string;
}): Promise<GeneratePeriodResult> => (await http.post("/billing/periods/generate", body)).data;

export const issueBill = async (id: number): Promise<BillDetail> =>
  (await http.post<BillDetail>(`/billing/bill/${id}/issue`)).data;

export const payBill = async (id: number): Promise<BillDetail> =>
  (await http.post<BillDetail>(`/billing/bill/${id}/pay`, {})).data;

export const voidBill = async (id: number): Promise<BillDetail> =>
  (await http.post<BillDetail>(`/billing/bill/${id}/void`)).data;

/** Public, unauthenticated — the same signed link a customer receives. */
export const fetchPublicBill = async (
  token: string,
  s?: AbortSignal,
): Promise<BillDetail> => (await http.get<BillDetail>(`/billing/b/${token}`, { signal: s })).data;
